import React, { useState, useId } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toneVars } from '../../design-system/agentTones';

/**
 * OrkaDisclosure — canonical progressive-disclosure primitive (PRD §4.3, §7.2).
 *
 * The single primitive used for:
 *   thinking panels, sources, decisions, action trails, rich-object expansions,
 *   failure breakdowns, dashboard pane summaries, etc.
 *
 *   <OrkaDisclosure title="Show thinking" tone="thinking" summary="3 steps">
 *     {steps.map(...)}
 *   </OrkaDisclosure>
 *
 * Supports nesting — children may themselves be disclosures.
 * Respects the agent-tone grammar via the `tone` prop.
 */
const TONE_KEYS = ['thinking', 'running', 'awaiting', 'done', 'failure'];

export function OrkaDisclosure({
  title,
  summary,
  tone = 'thinking',
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  children,
  className,
  contentClassName,
  testId,
}) {
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const panelId = useId();

  const toggle = () => {
    const next = !open;
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const safeTone = TONE_KEYS.includes(tone) ? tone : 'thinking';
  const vars = toneVars(safeTone);

  return (
    <div
      className={cn('rounded-xl border', className)}
      style={{
        ...vars,
        borderColor: 'var(--tone-border)',
        backgroundColor: 'var(--tone-soft)',
      }}
      data-testid={testId}
      data-tone={safeTone}
      data-state={open ? 'expanded' : 'collapsed'}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 rounded-xl',
          'orka-transition-colors orka-focus-ring',
          'hover:bg-black/5 dark:hover:bg-white/5',
          'text-left',
        )}
      >
        <ChevronRight
          width={14}
          height={14}
          aria-hidden="true"
          className="orka-transition-transform shrink-0"
          style={{
            color: 'var(--tone-color)',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        />
        <span className="text-xs font-semibold flex-1" style={{ color: 'var(--tone-color)' }}>
          {title}
        </span>
        {summary && (
          <span className="text-[11px] orka-text-tertiary truncate max-w-[60%]">{summary}</span>
        )}
      </button>
      {open && (
        <div
          id={panelId}
          role="region"
          className={cn(
            'px-3 pb-3 pt-1 border-t',
            contentClassName,
          )}
          style={{ borderTopColor: 'var(--tone-border)' }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default OrkaDisclosure;
